/**
 * StopDetailPanel — right panel showing details of the selected stop.
 *
 * Props:
 *   stop     {object|null} - selected stop (null = empty state)
 *   onUpdate {function}    - called with updated stop object
 *   onRemove {function}    - called to remove the stop
 *   baseUrl  {string}      - API base URL
 */

import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import DeleteIcon from '@material-ui/icons/Delete'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import PersonIcon from '@material-ui/icons/Person'
import ScheduleIcon from '@material-ui/icons/Schedule'

import StopFileUploader from './StopFileUploader'
import { formatTime } from './routeHelpers'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.disabled,
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
    opacity: 0.4,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(1.5),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  stopName: {
    flex: 1,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  sourceChip: {
    flexShrink: 0,
    height: 20,
    fontSize: '0.68rem',
  },
  sourceChipAspire: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  sourceChipManual: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: theme.spacing(0.5),
  },
  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
  infoIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    marginTop: 2,
    flexShrink: 0,
  },
  infoText: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  infoTextSecondary: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  timingGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: theme.spacing(1),
  },
  timingCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  timingCellLabel: {
    fontSize: '0.65rem',
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 2,
  },
  timingCellValue: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  divider: {
    margin: theme.spacing(1.5, 0),
  },
  inputFull: {
    width: '100%',
  },
  removeButtonRow: {
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  removeButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light + '1A',
      borderColor: theme.palette.error.dark,
    },
  },
}))

function TimingCell({ label, value }) {
  const classes = useStyles()
  return (
    <Box className={classes.timingCell}>
      <Typography className={classes.timingCellLabel}>{label}</Typography>
      <Typography className={classes.timingCellValue}>{value}</Typography>
    </Box>
  )
}

function StopDetailPanel({ stop, onUpdate, onRemove, baseUrl = '' }) {
  const classes = useStyles()
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  // Local editable state, synced when stop changes
  const [serviceMinutes, setServiceMinutes] = React.useState('')
  const [notes, setNotes] = React.useState('')

  // Use stopId + order as key so state resets when switching between stops,
  // even if two stops share the same _id (optimizer sets _id = siteId)
  const stopKey = stop ? `${stop.stopId || stop._id}-${stop.order || 0}` : null

  React.useEffect(() => {
    if (stop) {
      setServiceMinutes(stop.serviceMinutes != null ? String(stop.serviceMinutes) : '')
      setNotes(stop.notes || '')
    }
  }, [stopKey])

  if (!stop) {
    return (
      <Box className={`${classes.root} ${classes.emptyState}`}>
        <LocationOnIcon className={classes.emptyIcon} />
        <Typography variant="body2">Select a stop to view details</Typography>
      </Box>
    )
  }

  const isManual = stop.type === 'manual'

  const handleServiceMinutesBlur = () => {
    const val = parseInt(serviceMinutes, 10)
    const safeVal = isNaN(val) || val < 0 ? 0 : val
    if (safeVal !== stop.serviceMinutes) {
      if (onUpdate) onUpdate({ ...stop, serviceMinutes: safeVal })
    }
  }

  const handleNotesBlur = () => {
    if (notes !== (stop.notes || '')) {
      if (onUpdate) onUpdate({ ...stop, notes })
    }
  }

  const handleFilesChange = (updatedFiles) => {
    if (onUpdate) onUpdate({ ...stop, files: updatedFiles })
  }

  const handleRemoveConfirm = () => {
    setConfirmOpen(false)
    if (onRemove) onRemove(stop)
  }

  // Drive time: the stop itself stores driveTimeMinutes (from optimizer)
  const driveMin = stop.driveTimeMinutes != null ? `${stop.driveTimeMinutes} min` : '--'

  return (
    <Box className={classes.root}>
      <Box className={classes.scrollArea}>

        {/* ── Header: name + source chip ── */}
        <Box className={classes.header}>
          <Box className={classes.titleRow}>
            <Typography variant="h6" className={classes.stopName}>
              {stop.name || 'Unnamed Stop'}
            </Typography>
            <Chip
              label={isManual ? 'Manual' : 'Aspire'}
              size="small"
              className={`${classes.sourceChip} ${isManual ? classes.sourceChipManual : classes.sourceChipAspire}`}
            />
            {stop.actionType && stop.actionType !== 'generic' && (() => {
              const ACTION_LABELS = {
                work_ticket_visit: { label: 'Work Order', color: '#1565C0', bg: '#E3F2FD' },
                maintenance_visit: { label: 'Maintenance', color: '#2E7D32', bg: '#E8F5E9' },
                irrigation_inspection: { label: 'Irrigation', color: '#0277BD', bg: '#E1F5FE' },
                herbicide_application: { label: 'Herbicide', color: '#F57F17', bg: '#FFF8E1' },
                site_audit: { label: 'Site Audit', color: '#6A1B9A', bg: '#F3E5F5' },
                site_walk: { label: 'Site Walk', color: '#4527A0', bg: '#EDE7F6' },
                lead_capture: { label: 'New Lead', color: '#00838F', bg: '#E0F7FA' },
                customer_meeting: { label: 'Meeting', color: '#4E342E', bg: '#EFEBE9' },
                issue_resolution: { label: 'Issue', color: '#C62828', bg: '#FFEBEE' },
                assigned_visit: { label: 'Assigned Visit', color: '#AD1457', bg: '#FCE4EC' },
              }
              const cfg = ACTION_LABELS[stop.actionType]
              if (!cfg) return null
              return (
                <Chip
                  label={cfg.label}
                  size="small"
                  style={{ color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.color}`, fontWeight: 600, fontSize: '0.72rem', height: 22 }}
                />
              )
            })()}
          </Box>
        </Box>

        <Divider className={classes.divider} />

        {/* ── Address ── */}
        {stop.address ? (
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Address</Typography>
            <Box className={classes.infoRow}>
              <LocationOnIcon className={classes.infoIcon} />
              <Typography className={classes.infoText}>{stop.address}</Typography>
            </Box>
          </Box>
        ) : null}

        {/* ── Customer ── */}
        {stop.customerName ? (
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Customer</Typography>
            <Box className={classes.infoRow}>
              <PersonIcon className={classes.infoIcon} />
              <Typography className={classes.infoText}>{stop.customerName}</Typography>
            </Box>
          </Box>
        ) : null}

        {/* ── Timing (read-only) ── */}
        <Box className={classes.section}>
          <Typography className={classes.sectionLabel}>
            <Box component="span" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <ScheduleIcon style={{ fontSize: 12 }} />
              Estimated Times
            </Box>
          </Typography>
          <Box className={classes.timingGrid}>
            <TimingCell label="Arrive" value={formatTime(stop.estimatedArrival)} />
            <TimingCell label="Depart" value={formatTime(stop.estimatedDeparture)} />
            <TimingCell label="Drive" value={driveMin} />
          </Box>
        </Box>

        {/* ── Time Window (read-only, shown for assigned visits with constraints) ── */}
        {stop.timeWindow?.start && stop.timeWindow?.end && (
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>
              <Box component="span" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ScheduleIcon style={{ fontSize: 12 }} />
                Arrival Window
              </Box>
            </Typography>
            <Box style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 4,
              backgroundColor: stop.timeWindowWarning ? '#FFEBEE' : '#E8F5E9',
              border: `1px solid ${stop.timeWindowWarning ? '#EF9A9A' : '#A5D6A7'}`,
            }}>
              <Typography style={{ fontSize: '0.875rem', fontWeight: 600, color: stop.timeWindowWarning ? '#C62828' : '#2E7D32' }}>
                {formatTime(stop.timeWindow.start)} — {formatTime(stop.timeWindow.end)}
              </Typography>
              {stop.timeWindowWarning && (
                <Typography style={{ fontSize: '0.75rem', color: '#C62828', marginLeft: 'auto' }}>
                  {stop.timeWindowWarning}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── Action Context Details (for assigned visits) ── */}
        {stop.actionType === 'assigned_visit' && stop.actionContext && (
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Visit Details</Typography>
            {stop.actionContext.visitType && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoText} style={{ fontWeight: 500 }}>
                  Type: {stop.actionContext.visitType.replace(/_/g, ' ')}
                </Typography>
              </Box>
            )}
            {stop.actionContext.priority && stop.actionContext.priority !== 'normal' && (
              <Box className={classes.infoRow}>
                <Chip
                  label={stop.actionContext.priority.toUpperCase()}
                  size="small"
                  style={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    backgroundColor: stop.actionContext.priority === 'urgent' ? '#FFEBEE' : stop.actionContext.priority === 'high' ? '#FFF3E0' : '#F5F5F5',
                    color: stop.actionContext.priority === 'urgent' ? '#C62828' : stop.actionContext.priority === 'high' ? '#E65100' : '#616161',
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* ── Action Context Details (for issues) ── */}
        {stop.actionType === 'issue_resolution' && stop.actionContext && (
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Issue Details</Typography>
            {stop.actionContext.issueType && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoText}>
                  {stop.actionContext.issueType}
                </Typography>
              </Box>
            )}
            {stop.actionContext.issueSource && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoTextSecondary} style={{ fontSize: '0.75rem' }}>
                  Source: {stop.actionContext.issueSource.replace(/_/g, ' ')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── Action Context Details (for work tickets) ── */}
        {stop.actionType === 'work_ticket_visit' && stop.actionContext && (
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Work Ticket</Typography>
            {stop.actionContext.ticketNumber && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoText} style={{ fontWeight: 500 }}>
                  #{stop.actionContext.ticketNumber}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Divider className={classes.divider} />

        {/* ── Editable: service time ── */}
        <Box className={classes.section}>
          <Typography className={classes.sectionLabel}>Service Time</Typography>
          <TextField
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ min: 0, step: 5 }}
            value={serviceMinutes}
            onChange={(e) => setServiceMinutes(e.target.value)}
            onBlur={handleServiceMinutesBlur}
            className={classes.inputFull}
            InputProps={{
              endAdornment: <InputAdornment position="end">min</InputAdornment>,
            }}
          />
        </Box>

        {/* ── Editable: notes ── */}
        <Box className={classes.section}>
          <Typography className={classes.sectionLabel}>Notes</Typography>
          <TextField
            variant="outlined"
            size="small"
            multiline
            rows={3}
            placeholder="Add notes for this stop…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            className={classes.inputFull}
          />
        </Box>

        <Divider className={classes.divider} />

        {/* ── File uploader ── */}
        <Box className={classes.section}>
          <Typography className={classes.sectionLabel}>Attachments</Typography>
          <StopFileUploader
            files={stop.files || []}
            onFilesChange={handleFilesChange}
            baseUrl={baseUrl}
          />
        </Box>

      </Box>

      {/* ── Remove stop button ── */}
      <Box className={classes.removeButtonRow}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DeleteIcon />}
          className={classes.removeButton}
          onClick={() => setConfirmOpen(true)}
          fullWidth
        >
          Remove Stop
        </Button>
      </Box>

      {/* ── Confirm dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Remove Stop?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove <strong>{stop.name || 'this stop'}</strong> from the route? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirm} style={{ color: '#d32f2f' }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default StopDetailPanel
